import { extendObservable, toJS, observable, observe } from 'mobx'
import get from 'lodash/get'
import omit from 'lodash/omit'
import find from 'lodash/find'
import flatten from 'lodash/flatten'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import debounce from 'lodash/debounce'
import chunk from 'lodash/chunk'

import {
  expertApi,
  massProcessingApi,
  projectApi,
  segmentApi
} from '@atheneum/api'

import { ExpertOptionCondition } from '../Expert/components/ExpertBulkCreate/helpers/expertOptionCondition'
import { ExpertOptionField } from '../Expert/components/ExpertBulkCreate/helpers/expertOptionField'
import { ExpertFieldOptions } from '../Expert/components/ExpertBulkCreate/helpers/expertFieldOptions'
import {
  InitializeProcessingActionEnum,
  sendInitializeProcessingMetric
} from '../Expert/components/ExpertBulkCreate/helpers/expertBulkCreate.metrics'

import { formatMonthYear } from '@atheneum/api/dist/lib'
import { isUndefined } from 'lodash'

async function loadListItems(listId, offset, items = []) {
  const res = await massProcessingApi.getListItems(listId, offset)
  const newOffset = offset + 50
  if (res.count > newOffset) {
    return await loadListItems(listId, newOffset, [...items, ...res.rows])
  } else {
    return [...items, ...res.rows]
  }
}

async function getList(id) {
  const list = await massProcessingApi.getList(id)
  const items = await loadListItems(id, 0)
  return { ...list, listItems: items }
}

const emptyQuery = {
  keyword: '',
  expertData: '',
  companies: [],
  positions: [],
  country: [],
  industry: [],
  languages: [],
  proficiency: '',
  interviewed: false,
  lastInterviewedIn: [],
  experiencePeriod: 'ALL',
  sortBy: 'MATCH',
  hasEmail: false,
  hasApp: false,
  hasPersonalPhone: false,
  hasOfficePhone: false,
  searchServiceRequestId: null,
  hasMobilePhone: false,
  hasDirectPhone: false,
  hasWhatsapp: false,
  similarTo: [],
  suggestedFor: [],
  skip: 0,
  complianceFilter: 0,
  hfComplianceFilter: 0,
  expertStatus: {
    coldProspect: false,
    warmProspect: false,
    dormantProspect: false,
    activeExpert: false,
    dormantExpert: false,
    notContact: false
  },
  includeScreeningInformation: false,
  createdIn: []
}

class ExpertSearchStore {
  constructor(socket, expertBulkCreateStore) {
    if (localStorage.getItem('openLinkedinOnPin') === null) {
      localStorage.setItem('openLinkedinOnPin', 'true')
    }

    this.socket = socket
    extendObservable(this, {
      expertBulkCreateStore,
      experts: [],
      query: emptyQuery,
      sortOptions: [],
      companyRecomandations: [],
      shouldShowPinnedExperts:
        localStorage.getItem('expertSearchStoreShowPinned') === 'true',
      positionRecomandations: [],
      selected: null,
      pinnedExperts: [],
      expertsUpdateStatus: [],
      total: null,
      zoomTo: false,
      massProcessingLists: [],
      massProcessingList: null,
      loading: false,
      loadMore: false,
      totalExpertsLoaded: 0,
      didSearch: false,
      error: null,
      sherlockLoading: false,
      sherlockCheckList: false,
      lastListLoaded: false,
      expertBulkCreateInProgressPinIdentifiers: [],
      expertBulkCreateCompletedPinIdentifiers: [],
      saveItemRequestsRunning: 0,
      openLinkedinOnPin: localStorage.getItem('openLinkedinOnPin') === 'true'
    })

    // List of experts to pin
    this.expertsToPin = observable.shallowArray([])
    observe(this.expertsToPin, change => {
      if (change.added.length > 0) {
        /*this.expertBulkCreateInProgressPinIdentifiers.push(
          ...change.added.map(e => e.bulkCreateCustomIdentifier)
        )*/

        this.pinExpertsDebounced()
        if (this.expertsToPin.length >= 20) {
          this.pinExpertsDebounced.flush()
        }
      }
    })

    this.completedExpertsDispose = this.expertBulkCreateStore.observeCompletedExperts(
      async experts => {
        const expertsToProcess = experts.filter(
          e =>
            this.expertBulkCreateInProgressPinIdentifiers.includes(
              e.customIdentifier
            ) &&
            !this.expertBulkCreateCompletedPinIdentifiers.includes(
              e.customIdentifier
            )
        )

        if (expertsToProcess.length > 0) {
          this.expertBulkCreateCompletedPinIdentifiers.push(
            ...expertsToProcess.map(e => e.customIdentifier)
          )

          await this.finalizePinExperts(expertsToProcess.map(e => e.data))
        }
      }
    )

    this.errorExpertsDispose = this.expertBulkCreateStore.observeErrorExperts(
      experts => {
        const expertsToProcess = experts.filter(
          e =>
            this.expertBulkCreateInProgressPinIdentifiers.includes(
              e.customIdentifier
            ) &&
            !this.expertBulkCreateCompletedPinIdentifiers.includes(
              e.customIdentifier
            )
        )
        if (expertsToProcess.length > 0) {
          this.expertBulkCreateCompletedPinIdentifiers.push(
            ...expertsToProcess.map(e => e.customIdentifier)
          )

          let allExpertIds = []
          for (let expert of expertsToProcess) {
            allExpertIds.push(expert.data.id)
            if (expert.data.pdlId) {
              allExpertIds.push(expert.data.pdlId)
            }
          }

          this.experts.replace(
            this.experts.filter(e => !allExpertIds.includes(e.id))
          )
        }
      }
    )

    this.cancelledExpertsDispose = this.expertBulkCreateStore.observeCancelledExperts(
      experts => {
        const expertsToProcess = experts.filter(
          e =>
            this.expertBulkCreateInProgressPinIdentifiers.includes(
              e.customIdentifier
            ) &&
            !this.expertBulkCreateCompletedPinIdentifiers.includes(
              e.customIdentifier
            )
        )
        if (expertsToProcess.length > 0) {
          this.expertBulkCreateCompletedPinIdentifiers.push(
            ...expertsToProcess.map(e => e.customIdentifier)
          )

          let allExpertIds = []
          for (let expert of expertsToProcess) {
            allExpertIds.push(expert.data.id)
            if (expert.data.pdlId) {
              allExpertIds.push(expert.data.pdlId)
            }
          }

          this.experts.replace(
            this.experts.filter(e => !allExpertIds.includes(e.id))
          )
        }
      }
    )

    this.query.expertStatus.activeExpert = true
    //this.search()

    this.socket.on('expertUpdateDone', ({ expertId }) => {
      const list = toJS(this.expertsUpdateStatus).filter(id => id !== expertId)
      this.expertsUpdateStatus.replace(list)
    })
  }

  pinExpertsDebounced = debounce(
    () => {
      this.pinExperts()
    },
    500,
    { maxWait: 5000 }
  )

  pinExperts = async () => {
    const expertsToPinCopy = toJS(this.expertsToPin.map(e => e.data))
    this.expertsToPin.clear()

    const createdExperts = expertsToPinCopy.filter(e => Number(e.id))
    const pdlExperts = expertsToPinCopy.filter(e => !Number(e.id))

    const expertsToPin = []

    const pdlExpertChunks = chunk(pdlExperts, 25)
    for (let chunk of pdlExpertChunks) {
      const result = await expertApi.getPDLExperts(chunk.map(e => e.id))
      for (let expert of result) {
        const allEmails = []
        if (expert.emails) {
          allEmails.push(...expert.emails)
        }
        if (expert.email) {
          allEmails.push(expert.email)
        }

        const checkResult = await expertApi.checkExpert({
          emails: allEmails.length ? allEmails : undefined,
          fullName: expert.fullName,
          linkedinVanityName: expert.linkedinVanityName,
          link: expert.linkedinVanityName
            ? `https://www.linkedin.com/in/${expert.linkedinVanityName}`
            : undefined
        })

        if (
          checkResult &&
          !Array.isArray(checkResult) &&
          Number.parseInt(checkResult.id)
        ) {
          if (!createdExperts.some(e => checkResult.id === e.id)) {
            createdExperts.push(checkResult)
          }
        } else {
          expertsToPin.push(expert)
        }
      }
    }

    const createdExpertChunks = chunk(createdExperts, 25)
    for (let chunk of createdExpertChunks) {
      const result = await expertApi.getManyById(chunk.map(e => e.id))
      expertsToPin.push(...result)
    }

    await this.finalizePinExperts(expertsToPin)

    /*sendInitializeProcessingMetric(
      InitializeProcessingActionEnum.EXPERT_SEARCH_PIN
    )

    await this.expertBulkCreateStore.addExpertsToProcessingQueue(
      expertsToPinCopy,
      new ExpertFieldOptions(ExpertOptionCondition.And, [
        ExpertOptionField.FirstName,
        new ExpertFieldOptions(ExpertOptionCondition.Or, [
          ExpertOptionField.Email,
          ExpertOptionField.PhoneNumber
        ])
      ]),
      null,
      true,
      false,
      true
    )*/
  }

  loadLists = async () => {
    this.lastListLoaded = false
    let lists = await massProcessingApi.getLists('expert')
    if (!lists.length) {
      const list = await massProcessingApi.createList({
        name: 'My list',
        type: 'expert'
      })
      lists = [list]
    }
    this.massProcessingLists.replace(lists)
    this.setDefaultList()
  }

  setAthenaList = async list => {
    this.lastListLoaded = false
    if (!!list && list.id === 'new') {
      const newList = await massProcessingApi.createList({
        name: list.name,
        type: 'expert'
      })

      localStorage.setItem('lastMassProcessingList', JSON.stringify(newList))
      this.massProcessingLists.push(newList)
      this.massProcessingList = newList
      this.lastListLoaded = true
      this.pinnedExperts.replace([])
    } else {
      localStorage.setItem('lastMassProcessingList', JSON.stringify(list))
      this.massProcessingList = await getList(list.id)
      this.pinnedExperts.replace(
        this.massProcessingList.listItems
          .filter(i => i.data?.pinned)
          .map(p => ({
            pinned: true,
            ...p
          }))
      )
      this.lastListLoaded = true
    }
  }

  setDefaultList = async () => {
    const defaultListId =
      JSON.parse(localStorage.getItem('lastMassProcessingList'))?.id ||
      this.massProcessingLists[0]?.id
    const defaultListExists = this.massProcessingLists.find(
      l => l.id === defaultListId
    )

    if (defaultListId && defaultListExists) {
      this.massProcessingList = await getList(defaultListId)
      this.pinnedExperts = this.massProcessingList.listItems
        .filter(i => i.data?.pinned)
        .map(p => ({
          pinned: true,
          ...p
        }))
      this.lastListLoaded = true
    } else if (!!this.massProcessingLists[0]?.listItems) {
      this.massProcessingList = this.massProcessingLists[0]
      this.pinnedExperts = this.massProcessingLists[0].listItems
        .filter(i => i.data?.pinned)
        .map(p => ({
          pinned: true,
          ...p
        }))
      this.lastListLoaded = true
    } else {
      this.massProcessingList = this.massProcessingLists[0]
      this.lastListLoaded = true
    }
  }

  getHighlightsRegexp(
    expertId,
    returnArray = false,
    returnScreeningOnly = false
  ) {
    try {
      const expert = find(toJS(this.experts), { id: expertId })
      if (!expert || !expert.highlight) return returnArray ? [] : null
      const reg = /<match>(.*?)<\/match>/gi

      let expertHighlight = toJS(expert.highlight || {})

      if (expert.highlight) {
        const keys = [
          'experiences.company',
          'experiences.position',
          'experiences.description',
          'industryExpertise',
          'expertExperience'
        ]

        Object.keys(toJS(expert.highlight)).forEach(key => {
          if (keys.includes(key)) {
            expertHighlight[key] = toJS(expert.highlight)[key]
          }
        })
      }

      if (returnScreeningOnly) {
        let filteredExpertHighlight = {}

        Object.keys(expertHighlight).forEach(key => {
          if (key.includes('screening')) {
            filteredExpertHighlight[key] = expertHighlight[key]
          }
        })

        expertHighlight = filteredExpertHighlight
      }

      const highlights = uniq(
        flatten(
          flatten(Object.values(expertHighlight)).map(i => {
            const str = i.replace(/<\/match> <match>/gi, ' ')
            const matches = []
            let x
            while ((x = reg.exec(str))) {
              matches.push(x[1])
            }
            return matches
          })
        )
      )

      if (returnArray) {
        return highlights
      }

      return this.buildRegexp(highlights)
    } catch (err) {
      return returnArray ? [] : null
    }
  }

  getExpertHighlight(expertId) {
    const keys = [
      'experiences.company',
      'experiences.position',
      'experiences.description',
      'industryExpertise',
      'expertExperience',
      'screening.screening',
      'screening.question',
      'screening.answer'
    ]

    let expertHighlight = {}
    const expert = find(this.experts, { id: expertId })
    if (expert.highlight) {
      Object.keys(toJS(expert.highlight)).forEach(key => {
        if (keys.includes(key)) {
          expertHighlight[key] = toJS(expert.highlight)[key]
        }
      })
    }

    return expertHighlight
  }

  buildRegexp = matches =>
    new RegExp('\\b' + matches.join('\\b|\\b') + '\\b', 'gi')

  selectExpert(id) {
    this.selected = id
  }

  clear() {
    this.skipSearch = true
    this.query = emptyQuery
    this.experts.replace([])
    this.didSearch = false
    this.skipSearch = false
    this.totalExpertsLoaded = 0
  }

  extractValues(query) {
    query.companies = query.companies.map(c => c.value)
    query.positions = query.positions.map(c => c.value)
    query.country = query.country.map(c => c.value)
    query.industry = query.industry.map(c => c.value)
    query.languages = query.languages.map(c => c.name)
    query.proficiency = query.proficiency.name
    query.similarTo = query.similarTo.map(c => c.value)
    query.suggestedFor = query.suggestedFor.map(c => c.value)
  }

  expertsAutocomplete(value) {
    if (!value) return Promise.resolve([])
    return expertApi.typeahead(value).then(options => {
      return options.map(i => {
        return {
          value: i.id,
          label: i.fullName
        }
      })
    })
  }

  projectsAutocomplete(value) {
    if (!value) return Promise.resolve([])
    return projectApi.typeahead(value).then(options => {
      return options.map(i => {
        return {
          value: i.id,
          label: i.projectName
        }
      })
    })
  }

  autocomplete(value, key) {
    if (!value) return Promise.resolve([])
    const searchKey = key === 'company' ? 'companies' : 'positions'
    const query = toJS(this.query)
    this.extractValues(query)
    delete query[searchKey]
    query.typeaheadType = key
    query.typeaheadQuery = value
    return expertApi
      .searchExperts({ ...query, aggs: true })
      .then(res => {
        return this.buildRecomendations(res, key)
      })
      .catch(err => {
        console.error('error', err)
      })
  }

  buildRecomendations(res, key) {
    const options = get(res, 'aggregations.experiences.' + key)

    return options.buckets
      .map(i => {
        return {
          value: i.key,
          label: `${i.key} (${i.experts.doc_count})`
        }
      })
      .filter(i => i.value !== 'Not specified')
  }

  buildSortOptions() {
    const OPTIONS = {
      DATEINTERVIEWED: {
        id: 'DATEINTERVIEWED',
        label: 'by engagement recency'
      },
      MATCH: {
        id: 'MATCH',
        label: 'by best match'
      },
      EXPERIENCE: {
        id: 'EXPERIENCE',
        label: 'by position recency'
      },
      NOINTERVIEWS: {
        id: 'NOINTERVIEWS',
        label: 'by no. of interviews'
      }
    }

    const options = [
      OPTIONS.MATCH,
      OPTIONS.DATEINTERVIEWED,
      OPTIONS.NOINTERVIEWS
    ]

    if (this.query.companies.length || this.query.positions.length) {
      options.push(OPTIONS.EXPERIENCE)
    }

    if (this.isSortByChangedByUser) {
      const isSortOptionAvaliable = find(
        options,
        so => so.id === this.query.sortBy
      )

      if (!isSortOptionAvaliable) {
        this.isSortByChangedByUser = false
        this.setSortOption()
      }
    } else {
      this.setSortOption()
    }

    return options
  }

  setSortOption() {
    if (this.query.companies.length || this.query.positions.length) {
      this.query.sortBy = 'EXPERIENCE'
    } else if (this.query.interviewed) {
      this.query.sortBy = 'NOINTERVIEWS'
    } else {
      this.query.sortBy = 'MATCH'
    }
  }

  onChangeSortOrder(value) {
    this.query.sortBy = value
    this.isSortByChangedByUser = true
    this.search()
  }

  addHistory(history) {
    this.history = history
  }

  attachSherlock(experts) {
    this.sherlockCheckList = false
    this.sherlockLoading = true

    expertApi.checkSherlockList(experts).then(res => {
      experts.forEach(e => {
        e.sherlock = res.find(r => r.id === e.id)?.sherlock
      })
      this.experts.replace(experts)
      this.sherlockLoading = false
    })
  }

  search(pageOnly) {
    this.didSearch = true
    this.selectExpert(null)
    if (this.history) {
      this.history.push('/expert-search')
    }
    if (this.loading) {
      this.repeatSearch = true
      return
    }
    if (this.skipSearch) return
    if (!pageOnly) this.query.skip = 0
    this.loading = true
    this.total = null
    this.sortOptions = this.buildSortOptions()

    const query = toJS(this.query)
    query.pinnedExperts = this.getPinnedIds()

    this.extractValues(query)
    return expertApi
      .searchExperts(query)
      .then(async result => {
        let experts = this.formatExperts(result.hits.hits)
        this.totalExpertsLoaded = experts.length

        const pinnedExperts = this.pinnedExperts
          .filter(e => e.data)
          .map(e => {
            const { data } = e

            // Sometimes emails can be empty for pinned experts
            let emails = data.address?.emails[0]
              ? data.address?.emails
                  .filter(email => email.email)
                  .map(email => email.email)
              : []
            emails = data.email ? emails.concat(data.email) : emails

            return {
              ...data,
              links:
                data.links && data.links[0]
                  ? data.links
                      .filter(link => link.professionalLinkUrl)
                      .map(link => link.professionalLinkUrl)
                  : [],
              emails
            }
          })

        if (pinnedExperts && pinnedExperts.length) {
          // Filtering out already pinned experts
          experts = experts
            .filter(ex => ex)
            .filter(expert => {
              const linkedinUrls = []
              if (expert.linkedinVanityName) {
                linkedinUrls.push(
                  'https://www.linkedin.com/in/' + expert.linkedinVanityName
                )
              }

              if (Array.isArray(expert.links) && expert.links.length) {
                linkedinUrls.push(...expert.links)
              }

              const exists = pinnedExperts.some(
                ex =>
                  ex.fullName === expert.fullName &&
                  ((ex.linkedinVanityName &&
                    ex.linkedinVanityName === expert.linkedinVanityName) ||
                    this.findCommonElements(ex.links, linkedinUrls) ||
                    this.findCommonElements(ex.emails, expert.emails))
              )

              return !exists
            })
        }

        this.searchServiceRequestId = result.searchServiceRequestId

        this.experts.replace(experts)
        this.total = result.hits.total.value
        this.sherlockCheckList = true
      })
      .then(() => {
        this.loading = false
        if (this.repeatSearch) {
          this.repeatSearch = false
          this.search()
        }
      })
      .catch(err => {
        console.error(err)
        this.error = err
        this.loading = false
      })
  }
  findCommonElements(arr1, arr2) {
    if (isUndefined(arr1) || isUndefined(arr2)) {
      return false
    }

    return arr1.some(item => arr2.includes(item))
  }
  removeUsedRecomendations(recomendations, key) {
    const used = this.query[key].map(i => i.value.toUpperCase())
    return recomendations.filter(i => !used.includes(i.value.toUpperCase()))
  }

  fmtExperience(experience) {
    if (!experience) return null
    const ex = {}
    ex.company = experience.company
    ex.position = experience.position
    ex.start = experience.startDate
      ? formatMonthYear(experience.startDate)
      : 'Not specified'
    ex.end = experience.current ? 'Now' : formatMonthYear(experience.endDate)
    return ex
  }

  getRelevantExperience(expert) {
    let experiences = []
    if (get(expert, 'inner_hits.experiences')) {
      get(expert, 'inner_hits.experiences.hits.hits', []).forEach(function(e) {
        experiences.push(e._source)
      })

      get(expert, 'experiences.hits.hits', []).map(function(e) {
        return experiences.push(e._source)
      })
    }

    if (!experiences || !experiences.length) {
      experiences = get(expert, '_source.experiences')
    }

    const current = find(experiences, 'current')
    if (current) return current

    if (experiences) {
      experiences = experiences.sort(function(a, b) {
        if (a.startDate < b.startDate) return 1
        if (a.startDate > b.startDate) return -1
        return 0
      })
      return experiences[0]
    } else {
      return {}
    }
  }

  savePinned(experts) {
    return localStorage.setItem('pinnedExperts', JSON.stringify(experts))
  }

  finalizePinExperts = async experts => {
    if (!Array.isArray(experts) || !experts.length) {
      return
    }

    this.experts.replace(
      this.experts.filter(
        e => !experts.some(k => k.id === e.id || k.pdlId === e.id)
      )
    )

    const pinnedExperts = this.pinnedExperts
      .filter(e => e.data)
      .map(e => {
        const { data } = e

        // Sometimes emails can be empty for pinned experts
        let emails = data.address?.emails[0]
          ? data.address?.emails
              .filter(email => email.email)
              .map(email => email.email)
          : []
        emails = data.email ? emails.concat(data.email) : emails

        return {
          ...data,
          links:
            data.links && data.links[0]
              ? data.links
                  .filter(link => link.professionalLinkUrl)
                  .map(link => link.professionalLinkUrl)
              : [],
          emails
        }
      })

    experts = experts
      .filter(ex => ex)
      .filter(expert => {
        const linkedinUrls = []
        if (expert.linkedinVanityName) {
          linkedinUrls.push(
            'https://www.linkedin.com/in/' + expert.linkedinVanityName
          )
        }
        if (Array.isArray(expert.links) && expert.links.length) {
          linkedinUrls.push(
            ...expert.links
              .filter(e => e.professionalLinkUrl)
              .map(e => e.professionalLinkUrl)
          )
        }

        const exists = pinnedExperts.some(
          ex =>
            ex.fullName === expert.fullName &&
            ((ex.linkedinVanityName &&
              ex.linkedinVanityName === expert.linkedinVanityName) ||
              this.findCommonElements(ex.links, linkedinUrls) ||
              this.findCommonElements(ex.emails, expert.emails))
        )

        return !exists
      })

    if (!experts.length) {
      return
    }

    const preppedItems = await Promise.all(
      experts.map(async e => await this.prepExpertForPinning(e))
    )

    this.saveItemRequestsRunning = this.saveItemRequestsRunning + 1

    const preppedItemsChunked = chunk(preppedItems, 25)

    for (let chunk of preppedItemsChunked) {
      const newListItems = await massProcessingApi.createItemBulk(
        this.massProcessingList.id,
        chunk
      )

      this.pinnedExperts.replace([
        ...uniqBy(this.pinnedExperts, 'id'),
        ...newListItems.map(e => ({
          ...e,
          pinned: true
        }))
      ])
    }

    this.saveItemRequestsRunning = this.saveItemRequestsRunning - 1
  }
  async prepExpertForPinning(expert) {
    if (!expert.experience) {
      if (expert.experiences) {
        // Expert form saves data to experiences and not cv.experiences, if exists take this one as expert was most likely freshly updated
        expert.experience = {
          position: expert.experiences?.[0]?.position?.name,
          company: expert.experiences?.[0]?.company?.name,
          start: expert.experiences?.[0]?.startDate
            ? formatMonthYear(expert.cv.experiences[0].startDate)
            : '',
          end: expert.experiences?.[0]?.endDate
            ? formatMonthYear(expert.cv.experiences[0].endDate)
            : 'Now'
        }
      } else {
        expert.experience = {
          position: expert.cv?.experiences?.[0]?.position?.name,
          company: expert.cv?.experiences?.[0]?.company?.name,
          start: expert.cv?.experiences?.[0]?.startDate
            ? formatMonthYear(expert.cv.experiences[0].startDate)
            : '',
          end: expert.cv?.experiences?.[0]?.endDate
            ? formatMonthYear(expert.cv.experiences[0].endDate)
            : 'Now'
        }
      }
    }

    expert.position = expert.experience?.position
      ? expert.experience.position
      : expert.experiences?.[0]?.position?.name
    expert.company = expert.experience?.company
      ? expert.experience.company
      : expert.experiences?.[0]?.company?.name
    expert.expertStatusId = expert.expert?.expertStatusId

    if (!expert.country) {
      expert.country = expert.address?.country?.name
    }

    let fullExpertData = expert

    const multiEmails = [...(fullExpertData?.address?.emails ?? [])]

    return {
      firstName: fullExpertData.firstName,
      lastName: fullExpertData.lastName,
      email: fullExpertData.email,
      emails: !!fullExpertData?.address?.emails.length ? multiEmails : [],
      expert: fullExpertData.expert,
      ...expert,
      ...fullExpertData,
      _type: Number(expert.id) ? 'lp_record' : 'pdl_record',
      pinned: true,
      lp_id: Number(expert.id) ? expert.id : undefined,
      pdl_id: !Number(expert.id) ? expert.id : undefined,
      id: Number(expert.id) ? expert.id : undefined
    }
  }

  prepExpertForUnpinning(data) {
    const expert = data.data ? toJS(data.data) : toJS(data)

    const preppedExpert = {
      id: expert.id ?? expert.pdl_id,
      fullName: expert.fullName,
      firstName: expert.firstName,
      lastName: expert.lastName,
      expertStatusId: expert.expertStatusId ?? expert.expert?.expertStatusId,
      country: expert.country ?? expert.address?.country?.name,
      experience: expert.experience,
      experiences: expert.experiences
        ? expert.experiences.map(e => omit(e, 'description'))
        : expert.cv?.experiences?.map(e => omit(e, 'description')),
      pinned: expert.pinned,
      checked: expert.checked,
      sherlock: expert.sherlock
    }

    if (!preppedExpert.experience) {
      if (preppedExpert.experiences) {
        // Expert form saves data to experiences and not cv.experiences, if exists take this one as expert was most likely freshly updated
        preppedExpert.experience = {
          position: preppedExpert.experiences?.[0]?.position?.name,
          company: preppedExpert.experiences?.[0]?.company?.name,
          start: preppedExpert.experiences?.[0]?.startDate
            ? formatMonthYear(preppedExpert.cv.experiences[0].startDate)
            : '',
          end: preppedExpert.experiences?.[0]?.endDate
            ? formatMonthYear(preppedExpert.cv.experiences[0].endDate)
            : 'Now'
        }
      } else {
        preppedExpert.experience = {
          position: preppedExpert.cv?.experiences?.[0]?.position?.name,
          company: preppedExpert.cv?.experiences?.[0]?.company?.name,
          start: preppedExpert.cv?.experiences?.[0]?.startDate
            ? formatMonthYear(preppedExpert.cv.experiences[0].startDate)
            : '',
          end: preppedExpert.cv?.experiences?.[0]?.endDate
            ? formatMonthYear(preppedExpert.cv.experiences[0].endDate)
            : 'Now'
        }
      }
    }

    preppedExpert.position = preppedExpert.experience?.position
      ? preppedExpert.experience.position
      : preppedExpert.experiences?.[0]?.position?.name
    preppedExpert.company = preppedExpert.experience?.company
      ? preppedExpert.experience.company
      : preppedExpert.experiences?.[0]?.company?.name

    return preppedExpert
  }

  async unpinExpert(expert) {
    const originalExpert = this.prepExpertForUnpinning(expert)
    if (!originalExpert.pinned) return

    let foundCreatedExpert = null

    if (!originalExpert.id || !Number(originalExpert.id)) {
      try {
        const checked = await expertApi.checkExpert({
          emails: expert.data?.emails
            ? expert.data.emails.map(e => e.email)
            : undefined,
          fullName: expert.data?.fullName,
          linkedinVanityName: expert.data?.linkedinVanityName,
          link: expert.data?.linkedinVanityName
            ? `https://www.linkedin.com/in/${expert.data.linkedinVanityName}`
            : undefined
        })
        if (checked.id) {
          foundCreatedExpert = await expertApi.getOne(checked.id)
        }
      } catch (error) {
        console.error(error.message)
      }
    }

    this.pinnedExperts.replace(
      this.pinnedExperts.filter(e => e.id !== expert.id)
    )

    originalExpert.pinned = false

    // Items added to AL not linked to an expert, we just need to delete them instead of showing them in the expert list first
    if (foundCreatedExpert) {
      this.experts.replace(
        uniqBy(
          [this.prepExpertForUnpinning(foundCreatedExpert), ...this.experts],
          'id'
        )
      )
    } else if (originalExpert.id) {
      this.experts.replace(uniqBy([originalExpert, ...this.experts], 'id'))
    }

    return massProcessingApi.deleteItem(expert.id)
  }

  loadPinned() {
    const data = localStorage.getItem('pinnedExperts')
    if (!data) return []

    try {
      return JSON.parse(data).map(e => {
        e.checked = false
        return e
      })
    } catch (err) {
      console.error('Error fetching pinned experts')
      return []
    }
  }

  setExpertUpdatingStatus(expertId) {
    const list = toJS(this.expertsUpdateStatus)
    list.push(expertId)
    this.expertsUpdateStatus.replace(list)
  }

  async searchBySegment({ id }) {
    this.clear()

    const segment = await segmentApi.getOne(id)
    const companies = segment.companies.map(c => ({
      value: c.name,
      label: c.name
    }))
    this.query.companies.replace(companies)
    const positions = segment.positions.map(p => ({
      value: p.name,
      label: p.name
    }))
    this.query.positions.replace(positions)
    const country = segment.expertFocusRegions.map(p => ({
      value: p.name,
      label: p.name
    }))
    this.query.country.replace(country)
    segment.countries.forEach(p =>
      this.query.country.push({ value: p.name, label: p.name })
    )
  }

  loadMoreExperts = () => {
    this.query.skip = this.totalExpertsLoaded
    this.query.pinnedExperts = this.getPinnedIds()
    const query = toJS(this.query)
    this.extractValues(query)
    this.loadMore = true

    const pinnedExperts = this.pinnedExperts
      .filter(e => e.data)
      .map(e => {
        const { data } = e

        // Sometimes emails can be empty for pinned experts
        let emails = data.address?.emails[0]
          ? data.address?.emails
              .filter(email => email.email)
              .map(email => email.email)
          : []
        emails = data.email ? emails.concat(data.email) : emails

        return {
          ...data,
          links:
            data.links && data.links[0]
              ? data.links
                  .filter(link => link.professionalLinkUrl)
                  .map(link => link.professionalLinkUrl)
              : [],
          emails
        }
      })

    expertApi
      .searchExperts(query)
      .then(result => {
        let experts = this.formatExperts(result.hits.hits)

        this.totalExpertsLoaded = this.totalExpertsLoaded + experts.length

        if (pinnedExperts && pinnedExperts.length) {
          // Filtering out already pinned experts
          experts = experts
            .filter(ex => ex)
            .filter(expert => {
              const linkedinUrls = []
              if (expert.linkedinVanityName) {
                linkedinUrls.push(
                  'https://www.linkedin.com/in/' + expert.linkedinVanityName
                )
              }
              if (Array.isArray(expert.links) && expert.links.length) {
                linkedinUrls.push(...expert.links)
              }

              const exists = pinnedExperts.some(
                ex =>
                  ex.fullName === expert.fullName &&
                  ((ex.linkedinVanityName &&
                    ex.linkedinVanityName === expert.linkedinVanityName) ||
                    this.findCommonElements(ex.links, linkedinUrls) ||
                    this.findCommonElements(ex.emails, expert.emails))
              )

              return !exists
            })
        }

        const expertList = this.experts.concat(
          experts.filter(e => !this.experts.some(k => k.id === e.id))
        )
        this.experts.replace(expertList)
        this.total = result.hits.total.value
      })
      .then(() => {
        this.loadMore = false
      })
      .catch(err => {
        console.error(err)
        this.error = err
        this.loadMore = false
      })
  }

  formatExperts = results => {
    return results.map(e => {
      const expert = e._source
      expert.index = e._index
      expert.emails = expert.emails || []
      expert.links = expert.links || []
      expert.linkedinVanityName = expert.linkedinVanityName || ''

      try {
        expert.highlight = e.highlight
        expert.checked = false

        // Fetch relevant experience and format it for veiw
        expert.experience = this.fmtExperience(this.getRelevantExperience(e))

        // Format experience dates with moment
        expert.experienceFrom = formatMonthYear(
          get(expert, 'experiences[0].startDate')
        )
        expert.experienceTo = get(expert, 'experiences[0].endDate')
          ? formatMonthYear(get(expert, 'experiences[0].endDate'))
          : 'Now'
      } catch (error) {
        console.error(error.message)
      }

      return expert
    })
  }

  togglePinnedExperts = () => {
    this.shouldShowPinnedExperts = !this.shouldShowPinnedExperts
    localStorage.setItem(
      'expertSearchStoreShowPinned',
      this.shouldShowPinnedExperts
    )
  }

  clearPinned = () => {
    this.pinnedExperts.replace([])
    this.savePinned()
  }

  fetchHighlight = async expertId => {
    this.sortOptions = this.buildSortOptions()
    const query = toJS(this.query)
    query.pinnedExperts = this.getPinnedIds()
    this.extractValues(query)
    const expert = await expertApi.searchExperts({
      ...query,
      highlightFor: expertId
    })
    const expertRecord = find(this.experts, e => e.id === expertId)
    expertRecord.highlight = expert.hits.hits[0].highlight
  }

  getExpertHighlights = expertId => {
    const expert = this.experts.find(expert => expert.id === expertId)

    let highlight = {}

    if (expert) {
      highlight = toJS(expert.highlight || {})
    }

    return highlight
  }
  getPinnedIds() {
    return this.pinnedExperts.map(p => p.data?.id || p.id).filter(x => x)
  }
}

export default ExpertSearchStore
